/**
 * Team Member Journey Tour Steps
 * Guides employees through their My (impact & growth)⁺ Journey dashboard
 */

import { DemoStep, clickTabAndWait, pollForElement, refreshDriver, ensureObjectiveConfigOpen, ensureObjectiveDashboardView } from './tourHelpers'

export const employeeJourneyDemoSteps: DemoStep[] = [
  // Step 1: Welcome
  {
    element: 'body',
    popover: {
      title: 'Welcome to Your Impact & Growth Journey',
      description: 'This tour will walk you through every feature on your Impact & Growth Journey — from setting objectives and tracking progress, to quarterly check-ins with your People Leader, gathering feedback from colleagues, and mapping out your personalised growth path. Let\'s explore!',
      side: 'bottom',
      align: 'center'
    }
  },

  // Step 2: Header Overview
  {
    element: '[data-tour="employee-header"]',
    popover: {
      title: 'Your Journey at a Glance',
      description: 'This bar shows your Job Group, People Leader, current impact & growth year, and campus. It updates automatically from Dynamics F&O, so the information here is always in sync with HR records.',
      side: 'bottom'
    }
  },

  // Step 3: Year & Period Selectors (NEW)
  {
    element: '[data-tour="year-period-selectors"]',
    popover: {
      title: 'Year & Check-In Period',
      description: 'Use these selectors to switch between impact & growth years and check-in periods (Mid-Year or Year-End). Selecting a past year will show your historical impact & growth data imported from Dynamics F&O.',
      side: 'bottom',
      align: 'end'
    }
  },

  // Step 4: Objectives Tab
  {
    element: '[data-tour="tab-objectives"]',
    popover: {
      title: 'my Objectives',
      description: 'This is your main workspace for managing objectives and tracking progress. You\'ll see your Key Performance Areas (KPAs), each linked to individual objectives with underlying KPIs. You can configure objectives, add to your impact portfolio, and monitor your progress here.',
      side: 'bottom'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-tour="tab-objectives"]')
    }
  },

  // Step 5: Empty State — Getting Started (conditional: only if no objectives)
  {
    element: '[data-tour="empty-state"]',
    popover: {
      title: 'Getting Started',
      description: 'You don\'t have objectives yet — that\'s perfectly normal! Your People Leader will schedule a collaborative objective-setting session with you. Together you\'ll create meaningful objectives aligned with your role and the organisation\'s strategic objectives (Scale, Quality, Culture, Impact).',
      side: 'top'
    },
    onHighlighted: () => {
      // Ensure objectives tab is active and config view is closed (dashboard view)
      ensureObjectiveDashboardView()
    }
  },

  // Step 6: Configure Objectives (conditional: only if no objectives)
  // Clicking Next navigates into the configuration view
  {
    element: '[data-tour="configure-objectives"]',
    popover: {
      title: 'Configure Your Objectives',
      description: 'This is where objective-setting happens. Click <strong>Next</strong> to open the configuration view and see all the ways you can create objectives.',
      side: 'top'
    },
    onHighlighted: () => {
      // Ensure objectives tab is active and config view is closed so the button is visible
      ensureObjectiveDashboardView()
    },
    onDeselected: () => {
      const btn = document.querySelector('[data-tour="configure-objectives"]') as HTMLElement
      btn?.click()
    }
  },

  // Step 7: Add New Tab (conditional: inside objective configuration dialog)
  {
    element: '#tour-add-new-tab',
    popover: {
      title: 'Add New Objective',
      description: 'This tab gives you access to all objective creation methods. Click <strong>Next</strong> to see your options!',
      side: 'top',
      align: 'center'
    },
    onHighlighted: () => {
      // Ensure config view is open (handles backward navigation), then click the tab
      ensureObjectiveConfigOpen('#tour-add-new-tab', (tabButton) => {
        tabButton.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
        tabButton.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }))
        tabButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
        setTimeout(() => {
          tabButton.scrollIntoView({ behavior: 'instant', block: 'center' })
          refreshDriver()
          setTimeout(refreshDriver, 100)
        }, 200)
      })
    }
  },

  // Step 8: Objective Creation Options (conditional)
  {
    element: '#tour-objective-options',
    popover: {
      title: 'Your Options',
      description: 'Choose how to create objectives: start from scratch with full flexibility, use a pre-built template aligned to your role, carry forward from the prior year, or browse the shared objective library. Your People Leader can also cascade strategic objectives down to you.',
      side: 'right',
      align: 'start'
    },
    onHighlighted: () => {
      // Ensure config view is open, then click "Add New" sub-tab so options grid is visible
      ensureObjectiveConfigOpen('#tour-add-new-tab', (addNewTab) => {
        // Click the "Add New" sub-tab to reveal the options grid
        addNewTab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
        addNewTab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }))
        addNewTab.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
        // Now poll for the actual target element
        pollForElement('#tour-objective-options', (el) => {
          el.scrollIntoView({ behavior: 'instant', block: 'center' })
          refreshDriver()
          setTimeout(refreshDriver, 100)
          setTimeout(refreshDriver, 300)
        })
      })
    },
    onDeselected: () => {
      // Return to KPA view after showing options
      const returnBtn = document.querySelector('[data-tour="return-dashboard"]') as HTMLElement
      if (returnBtn) {
        returnBtn.click()
      }
    }
  },

  // Step 9: KPA Dashboard (conditional: only if objectives exist)
  {
    element: '[data-tour="kpa-view"]',
    popover: {
      title: 'my KPA Dashboard',
      description: 'Here are your Key Performance Areas with real-time progress tracking, risk indicators (on-track, at-risk, behind), and linked individual objectives. Click any KPA to dive deeper — you can view underlying KPIs, add to your impact portfolio, and track your progress against targets.',
      side: 'top'
    },
    onHighlighted: () => {
      // Ensure objectives tab is active and config view is closed (dashboard view)
      ensureObjectiveDashboardView()
    }
  },

  // Step 10: Quarterly Check-Ins Tab
  {
    element: '[data-tour="tab-planning"]',
    popover: {
      title: 'my Check-Ins',
      description: 'Self-reflections, impact portfolios, and 1:1 dialogues with your People Leader. Here you\'ll prepare for quarterly check-ins (Q1, Mid-Year, Q3, Year-End), submit impact portfolio entries for each check-in period, and view your complete check-in history. Your People Leader will schedule these sessions and you\'ll be notified when they\'re ready.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-tour="tab-planning"]')
    }
  },

  // Step 11: In-the-Moment Feedback Tab
  {
    element: '[data-tour="tab-feedback"]',
    popover: {
      title: 'my "In-the-Moment" Feedback',
      description: 'Ask any internal or external colleague for instant feedback at anytime — no need to wait for a formal check-in cycle. Send 360° feedback requests here, then view the responses here. This feedback becomes part of your impact portfolio and can be referenced during check-ins.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-tour="tab-feedback"]')
    }
  },

  // Step 12: Personalized Growth Tab
  {
    element: '[data-tour="tab-development"]',
    popover: {
      title: 'my Personalised Growth',
      description: 'Your curated growth journey of mindsets, skills, and behaviours. Track your strengths and growth opportunities, set career aspirations, and build your personalised growth plan. Growth actions identified during check-ins can be transferred here automatically.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-tour="tab-development"]')
    }
  },

  // Step 13: Profile Button
  {
    element: '[data-tour="profile-button"]',
    popover: {
      title: 'my Profile',
      description: 'Access your professional profile to update qualifications, career history, and manage your annual profile updates. Keeping this current helps your People Leader understand your background and growth desires.',
      side: 'left'
    }
  },

  // Step 14: Tour Complete
  {
    element: 'body',
    popover: {
      title: 'You\'re All Set!',
      description: 'You\'re ready to start your Impact & Growth Journey! Explore each tab at your own pace — your objectives, quarterly check-ins, feedback, and personalised growth are all here. You can restart this tour anytime using the <strong>Explore</strong> button in the header.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-tour="tab-objectives"]')
    }
  }
]
